import { Box, Button, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getRefundsRequest,
	getRefundsForFilterIdRequest,
	resetRefundsForFilterId,
} from 'redux/reducers/refunds/reducer';
import {
	getRefunds,
	getRefundsForFilterId,
	getRefundsForFilterIdLoader,
	getRefundsLoader,
} from 'redux/reducers/refunds/selectors';
import { exportReturnsRequest } from 'redux/reducers/emExports/reducer';
import { getExportReturnsLoader } from 'redux/reducers/emExports/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { TRefundsRow } from 'redux/reducers/refunds/types';
import { IPageResponse, TFilter } from 'services/constants/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import {
	getFiltersFromNavigateParams,
	getOrderByFromNavigateParams,
	getTableApiFiltersFromNavigateParams,
} from 'utils/FilterParams';
import { refundsStructure } from './refunds-structure';

const DEFAULT_ORDER_BY_FIELD = 'timestamp';

export const Refunds: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const tableStructure = refundsStructure as TTableStructure<keyof TRefundsRow>[];

	const refunds = useSelector(getRefunds);
	const refundsIsLoading = useSelector(getRefundsLoader);
	const refundsForFilterId = useSelector(getRefundsForFilterId);
	const refundsForFilterIdIsLoading = useSelector(getRefundsForFilterIdLoader);
	const exportLoading = useSelector(getExportReturnsLoader);
	const userInfo = useSelector(getUserInfo);

	const [prevSearch, setPrevSearch] = useState(navigateParams?.search);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);

	const tableItems = useMemo((): IPageResponse<TRefundsRow> | null => {
		if (!refunds) return null;

		const rows = refunds.data ?? [];
		const filterIdRows = refundsForFilterId?.data ?? [];

		if (filterIdRows.length) {
			const filterIdIds = new Set(filterIdRows.map((row) => row.id));
			return {
				data: [...filterIdRows, ...rows.filter((row) => !filterIdIds.has(row.id))],
				totalCount: refunds.totalCount ?? 0,
			};
		}

		return {
			data: rows,
			totalCount: refunds.totalCount ?? 0,
		};
	}, [refunds, refundsForFilterId]);

	const dispatchGetRefunds = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString =
			(searchStringRaw?.length ?? 0) > 2 || (searchStringRaw?.length ?? 0) === 0
				? searchStringRaw
				: '';

		let filters: TFilter[] = [];

		const orderBy = getOrderByFromNavigateParams(tableStructure, navigateParams, {
			defaultFieldName: DEFAULT_ORDER_BY_FIELD,
		});

		if (searchString) {
			// The search covers both the EAS Order Id and the Store Order #: the store order
			// number is matched by `like` below, the EAS order id — by a separate request
			// whose rows are put on top of the list.
			const idNum = Number(searchString[0] === '#' ? searchString.slice(1) : searchString);
			if (idNum) {
				dispatch(
					getRefundsForFilterIdRequest({
						filters: [{ fieldName: 'orderId', value: idNum, operator: '=' }],
						orderBy,
						pageNumber: 1,
						pageSize: PAGE_SIZE,
					}),
				);
			} else {
				dispatch(resetRefundsForFilterId());
			}

			filters = [
				{
					fieldName: 'externalOrderId',
					value: `%${searchString}%`,
					operator: 'like',
				},
			];
		} else {
			dispatch(resetRefundsForFilterId());
			filters = getFiltersFromNavigateParams(tableStructure, navigateParams);
		}

		dispatch(
			getRefundsRequest({
				filters,
				orderBy,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
			}),
		);
	};

	useEffect(() => {
		const prevParams = queryString.parse(prevSearch as string);
		const otherPrevParams = Object.entries(prevParams).filter(([key]) => key !== 'search');
		const otherPrevParamsString = otherPrevParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');
		const currentParams = queryString.parse(location.search);
		const otherCurrentParams = Object.entries(currentParams).filter(([key]) => key !== 'search');
		const otherCurrentParamsString = otherCurrentParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');

		if (
			prevParams.search !== currentParams.search ||
			((prevParams.search as string)?.length ?? 0) === 0 ||
			otherPrevParamsString !== otherCurrentParamsString
		) {
			dispatchGetRefunds();
		}
		setPrevSearch(location.search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleLink = (refundId: number) => {
		const orderId = tableItems?.data.find((row) => row.id === refundId)?.orderId;
		if (!orderId) return;
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/refund/${orderId}/${refundId}`);
	};

	const handleAction = () => {};

	const handleExportReturns = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString = (searchStringRaw?.length ?? 0) > 2 ? searchStringRaw : '';

		dispatch(
			exportReturnsRequest({
				emails: [userInfo?.email ?? ''],
				// The search covers the whole table, so while it is active the column filters
				// are not applied to the list on the screen — nor to the export.
				filter: searchString
					? []
					: getTableApiFiltersFromNavigateParams(tableStructure, navigateParams),
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams, {
					defaultFieldName: DEFAULT_ORDER_BY_FIELD,
				}),
				searchString,
			}),
		);
		setExportDialogOpen(false);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TRefundsRow>
				tableStructure={tableStructure}
				items={tableItems}
				itemsIsLoading={refundsIsLoading || refundsForFilterIdIsLoading}
				handleAction={handleAction}
				handleLink={handleLink}
				minWidth={1200}
				name="refund"
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						p: '2px 4px',
					}}
				>
					<Typography
						variant="h1"
						color="primary"
						sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', ml: 1 }}
					>
						Refunds
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						disabled={exportLoading}
						onClick={() => setExportDialogOpen(true)}
					>
						Export Refunds
					</Button>
				</Box>
			</EasTableWithFilter>
			<ConfirmationDialog
				open={exportDialogOpen}
				setClose={() => setExportDialogOpen(false)}
				handleConfirm={handleExportReturns}
				title="Export Refunds"
				text={`The export is prepared in the background, the file will be sent to the email ${userInfo?.email ?? ''}.`}
				confirmButtonText="Export"
				confirmDisabled={!userInfo?.email}
				confirmLoading={exportLoading}
			/>
		</Box>
	);
};
